import type { KnownIssueCard } from '@innspel/core';
import { knownIssueStatus } from '../generated/tokens.ts';
import { Btn, Card, H, Hint, Skeleton, StatusPill, Tekstknapp } from './primitiver.tsx';
import { tekst } from './tekster.ts';

/** DD-03: maks tre kort. Grensen er klientens — registeret kan ha flere. */
const MAKS_KORT = 3;

/**
 * 2c — kjente feil. Vises FØR feltet i feilsporet; ønskesporet hopper over
 * steget til kontrakten har ønskekort (DD-03, presisert 2026-09-14). Tre svar
 * per kort (DD-29): «Ja, jeg også» · «Ligner, men ikke helt» · «Nei, det er noe
 * annet», der det siste står i dialogens fot (C2). Kortet er en `group`, ikke én
 * knapp: tre svar må være tre fokusstopp, og hver knapp bærer kortets tittel i
 * etiketten.
 *
 * Presisert 2026-09-20 (resten etter R8.4): de tre svarene har samme trapp som widgeten og
 * wireframen (DD-63, DD-58) — INGEN av dem står i aksjonsfargen, for ingen av dem er
 * skjermens primærhandling. «Ja, jeg også» er `tonet` (`brandBg`/`brand`) fordi det er
 * valget som sparer innsenderen for å skrive, «Ligner, men ikke helt» er en TEKSTKNAPP i
 * `actionText` ved siden av, «Nei, det er noe annet» er sekundær med ramme i foten. Før
 * var «Ja, jeg også» fylt i aksjonsfargen og «Ligner» en rammeknapp som fylte halve raden;
 * de to flatene sa ulike ting om samme valg.
 *
 * Presisert 2026-09-14 (C6): kortet har ALLTID en statuspille — «Aktiv» eller
 * «Rettet» fra `knownIssueStatus` i temaet. Før sto pillen bare når kortet hadde
 * en versjon, så et aktivt kort viste ingen status i det hele tatt, og versjonen
 * spilte rollen som status. Nå er versjonen et tillegg (DD-30).
 *
 * Treffene er et tilbud, aldri en sperre: tom liste og feilet oppslag hoppes
 * over uten melding (håndteres i Widget).
 */
export function KjenteFeil({
  kort,
  spor,
  laster,
  sender,
  onJaOgsa,
  onLigner,
}: {
  kort: KnownIssueCard[];
  spor: 'feil' | 'onske';
  laster?: boolean;
  /** «Ja, jeg også» er underveis: alle svar låses, så ett trykk gir én bekreftelse (C7). */
  sender?: boolean;
  onJaOgsa: (k: KnownIssueCard) => void;
  onLigner: (k: KnownIssueCard) => void;
}) {
  const tittel = spor === 'onske' ? tekst.kjenteFeilTittelOnske : tekst.kjenteFeilTittelFeil;
  const under = spor === 'onske' ? tekst.kjenteFeilUnderOnske : tekst.kjenteFeilUnderFeil;

  return (
    <div className="innspel-stabel">
      <H sub={under}>{tittel}</H>

      {laster ? (
        <div className="innspel-stabel" data-gap="sm" role="status" aria-label={tekst.kjenteFeilLaster}>
          <Skeleton hoy />
          <Skeleton hoy />
        </div>
      ) : (
        kort.slice(0, MAKS_KORT).map((k) => {
          const o = knownIssueStatus[k.status as keyof typeof knownIssueStatus] ?? knownIssueStatus.aktiv;
          // DD-30: «Rettes i X» når aktiv, «Rettet i X» når fikset — et TILLEGG til statusen.
          const versjon = k.fixedIn ? (k.status === 'fikset' ? tekst.rettetI(k.fixedIn) : tekst.rettesI(k.fixedIn)) : undefined;
          const kontekst = versjon ? `${tekst.kjentFeilKort(k.title, o.etikett)}. ${versjon}` : tekst.kjentFeilKort(k.title, o.etikett);
          return (
            <Card key={k.id} role="group" ariaLabel={kontekst}>
              <p className="innspel-kjent-tittel">{k.title}</p>
              {k.workaround ? (
                <p className="innspel-kjent-omvei">
                  <strong>{tekst.omvei}</strong> {k.workaround}
                </p>
              ) : null}
              <div className="innspel-rad" data-gap="sm">
                <StatusPill ikon={o.ikon} etikett={o.etikett} fg={o.fg} bg={o.bg} />
                {versjon ? <Hint>{versjon}</Hint> : null}
              </div>
              <div className="innspel-kjent-svar">
                <Btn variant="tonet" testId={`ja-ogsa-${k.id}`} label={tekst.svarJa} ariaLabel={`${tekst.svarJa}. ${kontekst}`} disabled={sender} busy={sender} onClick={() => onJaOgsa(k)} />
                <Tekstknapp testId={`ligner-${k.id}`} label={tekst.svarLigner} ariaLabel={`${tekst.svarLigner}. ${kontekst}`} disabled={sender} onClick={() => onLigner(k)} />
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
}
